<template>
  <div class="blackjack-container">
    <!-- 1) Tela de criação de sessão -->
    <div class="header" v-if="!sessionCreated">
      <h1>Partida Multiplayer de 21 (Blackjack)</h1>
      <h2>Faça 21 ou chegue o mais próximo possível para ganhar</h2>
    </div>
    <div class="initial-screen" v-if="!sessionCreated">
      <div class="bet-area">
        <label>Valor da aposta:</label>
        <div class="styled-select">
          <select v-model.number="betAmount">
            <option v-for="v in betOptions" :key="v" :value="v">{{ v }}</option>
          </select>
        </div>
        <span class="balance">
          {{ userName }}: {{ userBalance }} <strong>Zephyr Points</strong>
        </span>
      </div>
      <div class="start-menu">
        <button class="btn" @click="createSession(true)">Partida Pública</button>
        <button class="btn" @click="createSession(false)">Partida Privada</button>
      </div>
    </div>

    <!-- 2) Lobby de espera -->
    <div class="initial-screen" v-else-if="!gameStarted">
      <div v-if="isPrivate" class="bet-area">
        <p><strong>Link:</strong> <a :href="sessionLink">{{ sessionLink }}</a></p>
        <p><strong>Senha:</strong> {{ sessionPassword }}</p>
      </div>
      <p>Link da sala: <a :href="sessionLink">{{ sessionLink }}</a></p>
      <p>Aguardando jogadores: {{ players.length }}/3</p>
      <ul>
        <li v-for="(p, idx) in players" :key="idx">{{ p }}</li>
      </ul>
    </div>

    <!-- 3) Área de jogo -->
    <div v-else class="game-area">
      <h2 class="table-title">Jack Black (21 Multiplayer)</h2>

      <div class="pot-solo">Valor da aposta: {{ betAmount }}</div>
      <div class="game-content">
        <div class="table-wrapper">
          <div class="table" :style="tableStyle"></div>
        </div>

        <!-- Mãos dos jogadores -->
        <div
          v-for="(hand, idx) in displayHands"
          :key="idx"
          :class="['hand', 'player', sideClasses[idx]]"
        >
          <div class="card-group">
            <template v-for="(card, i) in hand" :key="i">
              <!-- Revele todas as cartas se a partida acabou, senão só as do local -->
              <img
                v-if="gameOver || idx === 1"
                class="card"
                :src="cardImages[card.filename]"
                alt="carta do jogador"
              />
              <img
                v-else
                class="card back"
                :src="backOfCard"
                alt="carta oculta"
              />
            </template>
          </div>
          <div class="player-name">
            {{ displayPlayers[idx] }} 
          </div>

          <!-- Controles só para quem está na parte de baixo -->
          <div v-if="idx === 1" class="controls">
            <button
              class="btn"
              @click="doAction('hit')"
              :disabled="!isMyTurn || gameOver"
            >Comprar</button>
            <button
              class="btn"
              @click="doAction('stand')"
              :disabled="!isMyTurn || gameOver"
            >Parar</button>

            <div v-if="isHost && gameOver" class="host-controls">
              <button class="btn" @click="startNewGame">Iniciar nova partida</button>
              <button class="btn" @click="leaveRoom">Sair da sala</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal de resultado (só aparece depois de gameOver, então mãos já reveladas) -->
      <div class="modal" v-if="message">
        <p class="log" style="white-space: pre-line">{{ message }}</p>
        <p v-if="message.includes(userName)" style="font-weight: bold; margin-top: 0.5rem;">
          Você ganhou {{ betAmount * 3 }} Zephyr Points!
        </p>

        <button class="btn" @click="closeModal">Fechar</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import backOfCard from '@/assets/cassino/cartas/back.png'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute,useRouter } from 'vue-router'
const router = useRouter()
import tableImg from '@/assets/cassino/blackjack.png'
import {
  fetchMe,
  fetchBetBalance,
  createGameSession,
  joinGameSession,
  fetchGameSessionStatus,
  actionGameSession,
  restartGameSession, leaveGameSession
} from '@/services/api'

// — autenticação e usuário —
const token       = localStorage.getItem('jwt_token')
const userName    = ref('Você')
const userBalance = ref(0)

// — sessão e apostas —
const betOptions      = [100,200,300,500,700,1000]
const betAmount       = ref(100)
const sessionCreated  = ref(false)
const isPrivate       = ref(false)
const isHost          = ref(false)
const sessionLink     = ref('')
const sessionPassword = ref('')
const sessionId       = ref('')
const players         = ref([])

// — estado de jogo —
const playerHands  = ref([])
const gameStarted  = ref(false)
const gameOver     = ref(false)
const message      = ref('')
const currentPlayer= ref(0)

// — UI e assets —
const cardsContext = require.context('@/assets/cassino/cartas', false, /\.png$/)
const cardImages = {}
cardsContext.keys().forEach(k => {
  const file = k.replace('./','')
  cardImages[file] = cardsContext(k)
})
const tableStyle = computed(() => ({ backgroundImage: `url(${tableImg})` }))
const sideClasses        = ['left','bottom','right']
const localPlayerIndex   = computed(() => players.value.indexOf(userName.value))
const isMyTurn           = computed(() =>
  gameStarted.value &&
  !gameOver.value &&
  currentPlayer.value === localPlayerIndex.value
)

// reordena para exibir de forma consistente
const displayIndices = computed(() => {
  const i = localPlayerIndex.value
  return [ (i+2)%3, i, (i+1)%3 ]
})
const displayPlayers = computed(() =>
  displayIndices.value.map(i => players.value[i])
)
const displayHands = computed(() =>
  displayIndices.value.map(i => playerHands.value[i])
)
/* const displayTotals = computed(() =>
  displayIndices.value.map(i =>
    playerHands.value[i].reduce((sum, card) => sum + card.value, 0)
  )
) */

async function startNewGame() {
  try {
    await restartGameSession(sessionId.value)
    await pollStatus()
  } catch (err) {
    alert('Erro ao reiniciar partida: ' + err.message)
  }
}

async function leaveRoom() {
  try {
    await leaveGameSession(sessionId.value)
    sessionCreated.value = false
    isHost.value = false
    gameStarted.value = false
    gameOver.value = false
    players.value = []
    sessionId.value = ''
    message.value = ''
    if (pollInterval) clearInterval(pollInterval)
  } catch (err) {
    alert('Erro ao sair da sala: ' + err.message)
  }
}


// polling do estado da partida
let pollInterval = null
async function pollStatus() {
  try {
    const st = await fetchGameSessionStatus(sessionId.value)
    players.value       = st.players
    betAmount.value     = st.betAmount
    if (st.hands) {
      gameStarted.value   = true
      gameOver.value      = st.gameOver
      message.value       = st.message
      currentPlayer.value = st.currentPlayerIndex
      playerHands.value   = st.players.map(mid => st.hands[mid])
    }
    if (st.gameOver && st.message === 'A sala foi encerrada pelo anfitrião.' && !isHost.value) {
      alert('A sala foi encerrada pelo anfitrião.');
      router.push('/BlackjackMultiplayer');
    }
  } catch (e) {
    console.error(e)
  }
}
function startPolling() {
  pollInterval = setInterval(pollStatus, 1000)
}
onBeforeUnmount(() => {
  if (pollInterval) clearInterval(pollInterval)
})

// join via URL ou fetch de usuário
const route = useRoute()
onMounted(async () => {
  if (token) {
    try {
      const me  = await fetchMe(token)
      userName.value   = me.user.mid
      const bal = await fetchBetBalance(token)
      userBalance.value = parseInt(bal.user.pvalues, 10)
    } catch(e) {
      console.error(e)
    }
  }
  const q = route.query
  if (q.sessionId) {
    sessionId.value       = q.sessionId
    sessionPassword.value = q.password || ''
    sessionCreated.value  = true
    isHost.value          = false
    isPrivate.value       = !!q.password
    try {
      await joinGameSession(sessionId.value, sessionPassword.value, token)
      startPolling()
    } catch (err) {
      alert('Não foi possível entrar na sala: ' + err.message)
    }
  }
})

// criar sala
async function createSession(publicGame) {
  isHost.value    = true
  isPrivate.value = !publicGame
  try {
    const data = await createGameSession(
      { betAmount: betAmount.value, isPublic: publicGame },
      token
    )
    sessionLink.value      = data.link
    const urlObj           = new URL(data.link)
    sessionId.value        = urlObj.searchParams.get('sessionId')
    sessionPassword.value  = data.password || ''
    sessionCreated.value   = true
    startPolling()
  } catch (err) {
    alert('Erro ao criar sessão: ' + err.message)
  }
}

// ações hit/stand
async function doAction(action) {
  if (!isMyTurn.value || gameOver.value) return
  try {
    await actionGameSession(sessionId.value, action, token)
    await pollStatus()
  } catch (e) {
    alert(e.response?.data?.error || e.message)
  }
}

// fecha o modal de resultado
function closeModal() {
  message.value = ''
}
</script>


<style>
/* -- todo o CSS de BlackjackSolo.vue, sem referências a dealer -- */
.blackjack-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.header{ text-align:center;margin:1rem 0;}
.header h1{font-size:2rem;margin:.5rem 0;}
.header h2{font-size:1.25rem;color:#555;}
.initial-screen{ display:flex; flex-direction:column; align-items:center; gap:1.5rem; border:1px solid #ccc; padding:2.5rem; border-radius:8px; box-shadow:0 2px 12px rgba(0,0,0,0.1); background:var(--v-theme-surface); margin:3rem 0; width:720px;}
.bet-area{ display:flex; gap:1rem; align-items:center;}
.balance{ font-weight:bold;}
.start-menu{ display:flex; gap:1rem;}
.modal{ position:fixed; top:50%; left:50%; transform:translate(-50%,-50%); padding:1.5rem 2rem; width:300px; text-align:center; border-radius:8px; box-shadow:0 2px 10px rgba(0,0,0,0.3); background:var(--v-theme-surface); color:var(--v-theme-on-surface); z-index:10;}
.modal .log{ font-weight:bold; margin-bottom:.5rem;}
.game-area {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  position: relative; /* para posicionar as mãos relativas a este container */
}
.game-content{ display:flex; flex-direction:column; align-items:center;}
.table-wrapper{ position:relative; width:80vw !important; height:60vh !important;}
.table-wrapper .table{ width:100%; height:100%; background-size:contain !important; background-position:center !important; background-repeat:no-repeat !important;}
.hand.player {
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
}
.hand{ display:flex; flex-direction:column; align-items:center; z-index:1;}
.card-group{ display:flex; gap:10px;}
.card{ width:80px; border-radius:5px; box-shadow:0 2px 5px rgba(0,0,0,0.5); background-color:#fff;}
.player-name{ display:flex; align-items:center; margin-top:.5rem; font-weight:bold;}
.controls {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.btn {
  padding: .75rem 1.5rem;
  font-size: 1rem;
  border: none;
  border-radius: 4px;
  background-color: #1976d2;
  color: #fff;
  cursor: pointer;
}

.btn:disabled {
  background-color: #aaa;
  cursor: not-allowed;
}
.styled-select{ position:relative; width:120px; display:inline-block; vertical-align:middle;}
.styled-select select{ width:100%; padding:10px 36px 10px 12px; font-size:1rem; border:1px solid rgba(0,0,0,0.12); border-radius:4px; background-color:var(--v-theme-surface); appearance:none; cursor:pointer; background-image:url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cpath d='M7 10l5 5 5-5z' fill='rgba(0,0,0,0.54)'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 8px center;}
/* posições para 3 jogadores */
.hand.player.left {
  position: absolute;
  top: 50%;
  left: 10%;            /* aproximado */
  transform: translateY(-50%);
}
.hand.player.right {
  position: absolute;
  top: 50%;
  right: 0%;           /* aproximado */
  transform: translateY(-50%);
}

.hand.player.bottom {
  position: absolute;
  bottom: -45%;          /* aproximado */
  left: 50%;
  transform: translateX(-50%);
}
.host-controls {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: center;
}
.table-title {
  position: absolute;
  top: -2rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.5rem;
  font-weight: bold;
  color: #222;
}
</style>
